/* eslint-disable lyrahealth/no-react-native-text */
import React from 'react'

import { StyleSheet, Text, View } from '@react-pdf/renderer'
import { isEmpty, isNil } from 'lodash-es'

import { SummaryDetailProps } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/summaryDetail/SummaryDetail'

import { EligibilityCapsuleDetails } from '../redesigned/hooks/useEligibilityCapsuleDetails'

type BenefitsSummaryPDFProps = {
  customerName: string
  benefitsResetDate: string
  benefitsSummaryDetails?: SummaryDetailProps[]
  eligibilityCapsuleDetails: EligibilityCapsuleDetails[]
  sessionSummaryDetails?: SummaryDetailProps[]
  sessionSummaryTitle: string
  isLyraSelect: boolean
  hasEligibilityError: boolean
  clientFirstName?: string
  healthPlanStatus?: React.ReactNode
  isDAEngageProvider: boolean
}
// Define styles for the PDF section
const styles = StyleSheet.create({
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#01798D',
    marginTop: 20,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#01798D',
  },
  border: {
    borderBottomWidth: 1,
    borderBottomColor: '#DFDBD6',
    marginTop: 16,
    marginBottom: 16,
  },
  subheader: {
    fontSize: 16,
    color: '#1a1918',
    marginTop: 24,
  },
  subheaderDetails: {
    fontSize: 12,
    color: '#4f4d4a',
    marginTop: 8,
  },
  summaryDetailsContainer: {
    marginTop: 16,
    borderRadius: '16px',
    padding: '12px',
    backgroundColor: '#F7F6F5',
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    height: '50px',
  },
  summaryDetailContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '272px',
  },
  fieldRow: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  fieldLabel: {
    fontSize: 9,
    color: '#4f4d4a',
  },
  fieldValue: {
    marginTop: 4,
    color: '#1A1918',
  },
  warningContainer: {
    marginTop: 16,
    borderRadius: '16px',
    padding: '12px',
    backgroundColor: '#FFF1D6',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  warningContainerHeader: {
    fontSize: 10,
    color: '#8C351C',
    fontWeight: 'bold',
  },
  warningContainerText: {
    fontSize: 10,
    lineHeight: 1.5,
    color: '#8C351C',
  },
  benefitsSummaryContainer: {
    marginTop: 16,
    borderRadius: '16px',
    padding: '12px',
    backgroundColor: '#F7F6F5',
    display: 'flex',
    flexDirection: 'column',
  },
  healthStatusText: {
    fontSize: 11,
    color: '#4F4D4A',
  },
  programCoverageItemContainer: {
    marginTop: 10,
  },
  programCoverageLabel: {
    fontSize: 11,
    color: '#1A1918',
  },
  programCoverageDescription: {
    marginTop: 5,
    fontSize: 9,
    color: '#4f4d4a',
  },
})

// Client Profile Section Component for PDF Export
const SummaryDetailPDF: React.FC<SummaryDetailProps> = ({ label, value, sublabel }) => (
  <View style={styles.summaryDetailContainer}>
    <Text style={styles.fieldLabel}>{label}:</Text>
    <Text style={styles.fieldValue}>{value}</Text>
    {sublabel && <Text style={styles.fieldLabel}>{sublabel}</Text>}
  </View>
)

const BenefitsSummaryPDF: React.FC<BenefitsSummaryPDFProps> = ({
  customerName,
  benefitsResetDate,
  benefitsSummaryDetails,
  eligibilityCapsuleDetails,
  sessionSummaryDetails = [],
  sessionSummaryTitle,
  isLyraSelect,
  hasEligibilityError,
  clientFirstName,
  healthPlanStatus,
  isDAEngageProvider,
}) => {
  const extractText: (node: React.ReactNode) => string = (node: React.ReactNode) => {
    if (typeof node === 'string' || typeof node === 'number') {
      return `${node}`
    }
    if (Array.isArray(node)) {
      return node.map(extractText).join('')
    }
    if (node && typeof node === 'object' && 'props' in node && node.props?.text) {
      return node.props.text
    }
    return ''
  }

  if (isNil(benefitsSummaryDetails)) {
    return null
  }

  const plainHealthPlanStatus = extractText(healthPlanStatus)
  const lyraCarePrograms = eligibilityCapsuleDetails.filter((entry) => entry?.isLyraProgram)
  const extendedPrograms = eligibilityCapsuleDetails.filter((entry) => !entry?.isLyraProgram)

  return (
    <View>
      <Text style={styles.sectionTitle}>Benefits Summary</Text>

      <View wrap={false}>
        <Text style={styles.subheader}>{sessionSummaryTitle}</Text>
        <View style={styles.summaryDetailsContainer}>
          {sessionSummaryDetails.map((summaryDetail, index) => (
            <SummaryDetailPDF key={`summary-detail-${index}`} {...summaryDetail} />
          ))}
        </View>
      </View>

      <Text style={styles.subheader}>{`Sponsored benefits • ${customerName}`}</Text>
      <Text style={styles.subheaderDetails}>{`Benefits Reset Date: ${benefitsResetDate}`}</Text>
      {isLyraSelect && (
        <View style={styles.warningContainer} wrap={false}>
          <Text style={styles.warningContainerHeader}>Lyra Select Member</Text>
          <Text style={styles.warningContainerText}>
            {clientFirstName} has a limited number of EAP sessions (6-8) and programs (coaching and individual therapy
            only) available to them.
          </Text>
        </View>
      )}
      {hasEligibilityError && clientFirstName && !isDAEngageProvider && (
        <View style={styles.warningContainer} wrap={false}>
          <Text style={styles.warningContainerText}>
            {clientFirstName}’s eligibility not confirmed. Ask them to check their legal name and DOB in their profile.
            For more help, contact the Lyra Care Navigation Team. Do not schedule more sessions unless clinically
            necessary.
          </Text>
        </View>
      )}
      {hasEligibilityError && clientFirstName && isDAEngageProvider && (
        <View style={styles.warningContainer} wrap={false}>
          <Text style={styles.warningContainerText}>
            {clientFirstName}’s eligibility not confirmed. Ask them to check their legal name and DOB in their profile.
            For more help, submit a ticket through the Help Center.
          </Text>
        </View>
      )}
      {!isEmpty(benefitsSummaryDetails) ? (
        <View style={styles.benefitsSummaryContainer} wrap={false}>
          {benefitsSummaryDetails.map((summaryDetail, index) => (
            <SummaryDetailPDF key={`summary-detail-${index}`} {...summaryDetail} />
          ))}
          <View style={styles.border} />
          <Text style={styles.healthStatusText}>{plainHealthPlanStatus}</Text>
        </View>
      ) : (
        <View style={styles.warningContainer} wrap={false}>
          <Text style={styles.warningContainerText}>No coverage</Text>
        </View>
      )}
      {!isEmpty(lyraCarePrograms) && (
        <View>
          <Text style={styles.subheader}>Lyra Care programs</Text>
          <View>
            {lyraCarePrograms
              .filter((program) => !isNil(program))
              .map((program, index: number) => (
                <View style={styles.programCoverageItemContainer} key={`programCoverageItem-${index}`} wrap={false}>
                  <Text style={styles.programCoverageLabel}>{program?.label ?? ''}:</Text>
                  <Text style={styles.programCoverageDescription}>{program?.coverageDescription ?? ''}</Text>
                </View>
              ))}
          </View>
        </View>
      )}
      {!isEmpty(extendedPrograms) && (
        <View>
          <Text style={styles.subheader}>More eligible programs</Text>
          {extendedPrograms
            .filter((program) => !isNil(program))
            .map((program, index: number) => (
              <View style={styles.programCoverageItemContainer} key={`programCoverageItem-${index}`} wrap={false}>
                <Text style={styles.programCoverageLabel}>{program?.label ?? ''}:</Text>
                <Text style={styles.programCoverageDescription}>{program?.coverageDescription ?? ''}</Text>
              </View>
            ))}
        </View>
      )}
    </View>
  )
}

export default BenefitsSummaryPDF
